import { useSearchParams } from "react-router-dom";

/**
 * Reads the `?ad={token}` per-advertisement link token a buyer team's
 * `/buy/{slug}?ad=` landing page carries into the funnel (decision D2).
 *
 * The landing page credited the four-question lead to that advertisement; this
 * is what lets the FULL prequalification be credited to the same one, so a
 * team's cost-per-lead does not depend on whether the visitor stopped at the
 * short form or finished the long one.
 *
 * Mirrors `usePrequalBuyLandingToken` exactly, and for the same reasons: there
 * is nothing to resolve (the server is the only thing that ever looks at it, at
 * finalize, where it honours the token only for an advertisement of the team
 * whose site the completion came through and silently ignores everything
 * else), so the funnel neither validates nor blocks on it. An unknown, archived
 * or wrong-team token lands the submission unattributed, never rejected.
 *
 * ── NOT IN THE SESSION SCOPE ─────────────────────────────────────────────
 *
 * 🔴 Unlike `blt`, this is deliberately NOT a member of the funnel's session
 * fingerprint (`fingerprintScope` in prequalStorage.ts). A continuation token
 * names ONE applicant's lead, so two people handed the same tab with different
 * tokens are two applications. An advertisement token names a CAMPAIGN, and
 * every visitor who clicks that ad shares it — it tells applications apart
 * from nothing, and adding it would invalidate every in-flight blob for no
 * gain (see the note on adding a fingerprint member in prequalStorage.ts).
 *
 * ── THE TOKEN STAYS IN THE URL ───────────────────────────────────────────
 *
 * Not stripped after reading, matching `blt`. The steps of this funnel are
 * component state rather than routes, so nothing rebuilds the query string
 * between them and the parameter survives to finalize on its own. The value
 * is a public campaign identifier printed on the ad itself; there is nothing
 * to protect by hiding it from history or logs.
 */
export function usePrequalAdToken(): string | null {
  const [params] = useSearchParams();
  const raw = params.get("ad");
  // Blank normalises to null so `?ad=` with nothing after it sends nothing,
  // matching what the server's own normaliser would make of it.
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}
